import React from "react";
import { Link } from "react-router-dom";
import { ShieldQuestion } from "lucide-react";
import ResponsiveFaq from "@/components/common/ResponsiveFaq";
import faqContent from "@/lib/faq-content.json";

/**
 * Sponsor safety & FAQ.
 *
 * Every answer below describes only what the platform actually does today:
 * accounts are created with an email and password, sponsor profile details are
 * self-reported, availability is set by each sponsor, requests are accepted or
 * declined, and safety tools are Block / Report plus human moderation.
 * No vetting, verification, credentials, partnerships or outcomes are claimed.
 *
 * The questions and answers live in src/lib/faq-content.json so that the
 * visible copy and the homepage FAQPage structured data (emitted once by
 * src/lib/structured-data.js, client-side and at prerender time) can never
 * diverge. There are deliberately no hidden, schema-only FAQs.
 *
 * Layout: ResponsiveFaq shows every answer already open on phones (HeyCatch
 * recorded rage-clicks on the trust questions there) and keeps the accordion on
 * desktop. Same data, one layout mounted at a time. The wording is untouched -
 * the honest caveats are the point, and trimming them for a phone would weaken
 * them.
 */
export const SAFETY_FAQS = faqContent.homeSafetyFaqs;

/** The questions HeyCatch recorded rage-clicks on, surfaced first on phones. */
const MOBILE_PRIORITY_IDS = ["vetted", "privacy", "unavailable", "why-pay"];

export default function SafetyFaq() {
  return (
    <section
      data-testid="home-safety-faq"
      className="bg-white border-t border-[#E3DED4]"
      aria-labelledby="safety-faq-heading"
    >
      <div className="max-w-4xl mx-auto px-4 py-12 lg:py-16">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#F7F4EF] border border-[#E3DED4] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#1F6F78]">
          <ShieldQuestion className="w-3.5 h-3.5" />
          Sponsor safety &amp; FAQ
        </span>
        <h2
          id="safety-faq-heading"
          className="mt-4 font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]"
        >
          Honest answers before you reach out
        </h2>
        <p className="mt-2 text-[#4B5A60] leading-7">
          What this platform does, what it costs, and what it is not.
        </p>

        <ResponsiveFaq
          items={SAFETY_FAQS}
          testIdPrefix="home-faq"
          priorityIds={MOBILE_PRIORITY_IDS}
          className="mt-6"
        />

        <p className="mt-6 text-sm text-[#4B5A60]">
          Still unsure about something?{" "}
          <Link
            to="/contact"
            data-testid="home-faq-contact-link"
            className="font-semibold text-[#1F6F78] hover:underline"
          >
            Send us a message
          </Link>{" "}
          - no account needed.
        </p>
      </div>
    </section>
  );
}
